import axios from 'axios';

import { refreshTokens } from './authenticate';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
});

// ============================================================================
// Request interceptor: adiciona Authorization Bearer com o access token atual.
// ============================================================================
api.interceptors.request.use((request) => {
  const access = localStorage.getItem('vo_access_token');
  if (access) {
    request.headers.Authorization = `Bearer ${JSON.parse(access)}`;
  }
  return request;
});

// ============================================================================
// Response interceptor: ao receber 401, tenta refresh automatico do token.
// (FEAT-jwt-access-refresh, 2026-05-26.)
//
// - Se ja ha um refresh em curso, enfileira a request e aguarda.
// - Se sucesso: armazena novos tokens, refaz a request original.
// - Se falha: limpa storages e redireciona para "/" (logout completo).
// ============================================================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const fullLogoutAndRedirect = () => {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (e) {
    // ignora — vamos redirecionar de qualquer jeito
  }
  // Reuso da estrategia do signOut em auth-context.js: refresh completo
  // descarta state React e busca index.html atualizado.
  window.location.href = '/';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response ? error.response.status : null;

    if (status !== 401 || !originalRequest || originalRequest.retriedRefresh) {
      return Promise.reject(error);
    }

    // Nao tentar refresh em chamadas para o proprio endpoint de auth.
    const url = originalRequest.url || '';
    if (url.includes('/auth/login/token') || url.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    const refresh = localStorage.getItem('vo_refresh_token');
    if (!refresh) {
      // Sem refresh disponivel: sessao expirou, logout.
      fullLogoutAndRedirect();
      return Promise.reject(error);
    }

    // Ja ha um refresh em curso — entra na fila e aguarda o token novo.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newAccessToken) => {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          originalRequest.retriedRefresh = true;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest.retriedRefresh = true;
    isRefreshing = true;

    try {
      const data = await refreshTokens(JSON.parse(refresh));

      localStorage.setItem(
        'vo_access_token',
        JSON.stringify(data.access_token)
      );
      localStorage.setItem(
        'vo_refresh_token',
        JSON.stringify(data.refresh_token)
      );

      processQueue(null, data.access_token);

      originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      fullLogoutAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
